import { useEffect } from 'react';

/**
 * HubSpot live chat on the PUBLIC order pages (number picker, Select plan,
 * Checkout) for visitors who are not signed in.
 *
 * client/index.html loads the LimePhone portal script (24096276) with
 * loadImmediately:false, and until now only the authenticated layout called
 * widget.load(), so the order funnel never showed the launcher. Here it is
 * loaded ANONYMOUSLY: no email or name is passed to HubSpot before the
 * visitor has submitted anything. Leaving the order pages removes it again,
 * so the rest of the logged-out site is unchanged.
 *
 * The launcher is lifted above any fixed bottom bar on the page (.lp-sticky:
 * the number picker's and Select plan's continue bars) and above the Checkout
 * pay button ([data-chat-keep-clear]) whenever they reach into its corner, so
 * it never covers the continue/pay button.
 */

const ORDER_CHAT_ROUTES = new Set(['/', '/start', '/select-plan', '/checkout']);

/** Is this pathname one of the public order pages that shows the chat? */
export function isPublicOrderChatRoute(pathname: string): boolean {
  const p = (pathname || '/').replace(/\/+$/, '') || '/';
  return ORDER_CHAT_ROUTES.has(p);
}

/** Gap (px) between the launcher and the page bottom / a fixed bottom bar. */
export const CHAT_GAP_PX = 8;

/** Bottom offset for the launcher given the height of a fixed bottom bar. */
export function chatBottomOffset(stickyBarHeight: number): number {
  return stickyBarHeight > 0 ? Math.ceil(stickyBarHeight) + CHAT_GAP_PX : 0;
}

export interface Box { top: number; bottom: number; left: number; right: number }

/**
 * How far (px) to lift the launcher off the bottom so it covers none of the
 * keep-clear elements on screen: the fixed bottom bar (.lp-sticky) and the
 * Checkout pay button ([data-chat-keep-clear]). An element only counts when it
 * is in the launcher's column (right edge) and reaches into its zone at the
 * bottom of the viewport; then the launcher sits just above it.
 */
export function launcherOffset(
  viewport: { w: number; h: number },
  launcher: { width: number; height: number },
  keepClear: Box[],
): number {
  let offset = 0;
  for (const r of keepClear) {
    if (r.bottom <= 0 || r.top >= viewport.h) continue; // off screen
    const inColumn = r.right > viewport.w - launcher.width;
    const zoneTop = viewport.h - launcher.height;
    if (inColumn && r.bottom > zoneTop) offset = Math.max(offset, chatBottomOffset(viewport.h - r.top));
  }
  // Never push it off the top of the screen.
  return Math.min(offset, Math.max(0, viewport.h - launcher.height - CHAT_GAP_PX));
}

const CONTAINER_ID = 'hubspot-messages-iframe-container';
const KEEP_CLEAR = '.lp-sticky, [data-chat-keep-clear]';

function placeLauncher(): void {
  const container = document.getElementById(CONTAINER_ID);
  if (!container) return;
  const boxes: Box[] = [];
  document.querySelectorAll<HTMLElement>(KEEP_CLEAR).forEach((el) => {
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return;
    const r = el.getBoundingClientRect();
    if (r.width && r.height) boxes.push({ top: r.top, bottom: r.bottom, left: r.left, right: r.right });
  });
  const c = container.getBoundingClientRect();
  const offset = launcherOffset({ w: window.innerWidth, h: window.innerHeight }, { width: c.width, height: c.height }, boxes);
  // HubSpot styles the container with !important rules, so set ours the same way.
  const want = offset > 0 ? `${offset}px` : '';
  if (container.style.getPropertyValue('bottom') === want) return; // nothing moved
  if (want) container.style.setProperty('bottom', want, 'important');
  else container.style.removeProperty('bottom');
}

export function usePublicOrderChat(pathname: string): void {
  const onOrderPage = isPublicOrderChatRoute(pathname);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;

    const apply = () => {
      if (cancelled) return;
      const hs = window.HubSpotConversations;
      if (!hs?.widget) return;
      const loaded = !!hs.widget.status?.()?.loaded;
      if (onOrderPage) {
        // Anonymous: load() with no identity.
        if (loaded) hs.widget.refresh();
        else hs.widget.load();
      } else if (loaded) {
        hs.widget.remove();
      }
    };

    if (window.HubSpotConversations?.widget) apply();
    else if (onOrderPage) {
      // The script is injected when the page is idle; run once it is ready.
      window.hsConversationsOnReady = window.hsConversationsOnReady || [];
      window.hsConversationsOnReady.push(apply);
    }

    // Keep the launcher clear of the bottom bar / pay button: on scroll and
    // resize, and when the DOM changes (the bar appears with page state;
    // HubSpot inserts and restyles its container). One observer, throttled to
    // a frame; placeLauncher is a no-op when nothing moved, so its own style
    // write can't loop.
    let raf = 0;
    const schedule = () => { if (!raf) raf = window.requestAnimationFrame(() => { raf = 0; placeLauncher(); }); };
    let observer: MutationObserver | undefined;
    if (onOrderPage) {
      placeLauncher();
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      if (typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(schedule);
        observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
      }
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      // Leaving the order pages: drop our offset.
      document.getElementById(CONTAINER_ID)?.style.removeProperty('bottom');
    };
  }, [onOrderPage, pathname]);
}
